import { Bell, LogOut, Search, ChefHat, ShieldCheck, Logs, Menu } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { LogoutModel } from "../common/Model/LogoutModel";
import { useActiveRole } from "@/hooks/useActiveRole";
import { RootState, useAppSelector, useAppDispatch } from "@/redux/store";
import { getInitials, isTodayHoliday, isStoreClosed, showToastNotification } from "@/utility/helper";
import { CommonSelect } from "../common/CommonSelect";
import NotificationDropdown from "../NotificationDropdown";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import {
  fetchHolidaySettings,
  fetchNotificationCount,
  fetchUserDetails,
  setNotificationCount,
} from "@/redux/features/UserSlice";
import { Alert, INotificationListResponse } from "@/interfaces/common.interface";
import { getRolePrefix, RoutesLink } from "@/utility/RouteLinks";
import { StatusBadge } from "../common/StatusBadge";
import { useRoleNavigate } from "@/hooks/useRoleNavigate";
import { IconTooltip } from "../common/IconTooltip";
import { usePermissions } from "@/hooks/usePermissions";
import { StoreCloseModal } from "../common/Model/StoreCloseModal";

interface HeaderProps {
  mobileOpen: boolean;
  setMobileOpen: (value: boolean) => void;
}

export const Header: React.FC<HeaderProps> = ({ mobileOpen, setMobileOpen }) => {
  const dispatch = useAppDispatch();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [restrictionModal, setRestrictionModal] = useState({ open: false, title: '', message: '' });
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Alert[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const navigate = useRoleNavigate()
  const { isAdmin } = usePermissions();
  const { user, notificationCount, holidaySettings } = useAppSelector(
    (state: RootState) => state.user,
  );
  const roleName = user?.role?.name;

  useEffect(() => {
    dispatch(fetchUserDetails());
    if (!isAdmin) {
      dispatch(fetchHolidaySettings());
    }
    dispatch(fetchNotificationCount());
  }, []);


  useEffect(() => {
    if (!isAdmin) {
      if (user?.storeProfile && (user.storeProfile.status === 'online' || user.storeProfile.status === 'busy')) {
        if (holidaySettings && isTodayHoliday(holidaySettings)) {
          handleStatusUpdate('offline');
        } else if (user?.storeProfile?.settings && isStoreClosed(user.storeProfile)) {
          // console.log('Store is closed');
          handleStatusUpdate('offline');
        }
      }
    }
  }, [user?.storeProfile?.status, user?.storeProfile?.settings, holidaySettings]);

  const fetchNotifications = async () => {
    try {
      setNotifLoading(true);
      const response = await callApi<INotificationListResponse>(
        `${API_URL}${API_ENDPOINT.notification}/user`,
        { method: "GET" },
      );

      // Map the specific structure provided: { notifications: [...], pageInfo: {...} }
      const rawNotifications =
        response?.data?.notifications || [];

      const mappedAlerts: Alert[] = rawNotifications.map((n: any) => {
        let title = "Notification";
        if (n.message?.toLowerCase().includes("driver"))
          title = "Driver Update";
        else if (n.message?.toLowerCase().includes("order"))
          title = "Order Update";

        return {
          id: n.id,
          severity: "info" as const,
          title: title,
          message: n.message,
          read: n.seen,
          createdAt: n.createdAt,
          actionUrl: n.payload?.orderId ? RoutesLink.ViewOrders.replace(':id', n.payload?.orderId) : n.payload?.returnRequestId ? RoutesLink.ViewReturn.replace(":id", n.payload?.returnRequestId) : undefined
        };
      });

      setNotifications(mappedAlerts.slice(0, 5));
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    } finally {
      setNotifLoading(false);
    }
  };


  const handleStatusUpdate = async (newStatus: string) => {
    if (!user?.storeProfile?.id || statusLoading) return;
    const formData = new FormData();
    formData.append('storeId', user.storeProfile.id);
    formData.append('status', newStatus);

    setStatusLoading(true);
    try {
      await callApi(
        `${API_URL}${API_ENDPOINT.stores}/update`,
        {
          method: 'PUT',
          data: formData,
          multipart: true
        }
      );
      dispatch(fetchUserDetails());
      showToastNotification('success', `Store status updated to ${newStatus}`);
    } catch (error) {
      ThrowError(error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await callApi(`${API_URL}${API_ENDPOINT.notification}/user`, {
        method: "PATCH",
        data: {
          ids: [id],
          all: false,
          action: "seen",
        },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
      );
      if (notificationCount > 0) {
        dispatch(setNotificationCount(notificationCount - 1));
      }
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await callApi(`${API_URL}${API_ENDPOINT.notification}/user`, {
        method: "PATCH",
        data: {
          ids: [],
          all: true,
          action: "seen",
        },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      dispatch(setNotificationCount(0));
    } catch (error) {
      console.error("Failed to mark all notifications as read", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
      fetchNotifications();
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications]);

  const viewAllPath = RoutesLink.Notifications;






  return (
    <>
      <header className="h-14 flex items-center justify-between px-2 md:px-6 border-b bg-card flex-shrink-0">
        <div className="flex items-center gap-3">
          {/* Menu Sidebar Toggle - Mobile Only */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden group relative w-10 h-10 rounded-xl flex items-center justify-center hover:bg-secondary transition-all text-muted-foreground cursor-pointer"
          >
            <Menu
              size={20}
              className="group-hover:text-primary transition-colors"
            />
          </button>

          {!isAdmin && (
            <div className="flex items-center md:ml-2">
              <CommonSelect
                value={user?.storeProfile?.status == "created" ? "" : user?.storeProfile?.status || 'offline'}
                placeholder="Select Status"
                onValueChange={(val) => {
                  // if (val === 'online') {
                  // Check Holiday
                  if (holidaySettings && isTodayHoliday(holidaySettings) && holidaySettings?.status === "active") {
                    setRestrictionModal({
                      open: true,
                      title: "Store Holiday",
                      message: "Store is currently offline due to an active holiday schedule. Please contact the Admin to remove or update the holiday dates before setting the store online.",
                    });
                    return;
                  }
                  // Check Business Hours
                  if (user?.storeProfile?.settings && isStoreClosed(user.storeProfile as any)) {
                    setRestrictionModal({
                      open: true,
                      title: "Store Closed",
                      message: `The store's closing time has been exceeded. To bring the store online again, please go to Edit Profile and update the store's closing time.`
                    });
                    return;
                  }
                  // }
                  handleStatusUpdate(val);
                }}
                options={[
                  { value: 'online', label: '🟢 Online' },
                  { value: 'busy', label: '🟠 Busy' },
                  { value: 'offline', label: '🔴 Offline' },
                ]}
                triggerClassName="h-8 rounded-lg text-[11px] font-bold bg-secondary/10 hover:bg-secondary/20 border-secondary/20 w-28 uppercase tracking-wider"
                disabled={statusLoading}
              />
            </div>
          )}

          {isAdmin && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/5 border border-primary/20">
              <ShieldCheck size={14} className="text-primary" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
                System {roleName}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative" ref={dropdownRef}>
            <IconTooltip content="Notifications" side="bottom">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="group relative w-10 h-10 rounded-xl flex items-center justify-center hover:bg-secondary transition-all text-muted-foreground cursor-pointer"
              >
                <Bell
                  size={20}
                  className="group-hover:text-primary transition-colors"
                />
                {notificationCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-destructive text-[10px] font-bold text-white flex items-center justify-center rounded-full border-2 border-card animate-in fade-in zoom-in duration-300">
                    {notificationCount > 9 ? "9+" : notificationCount}
                  </span>
                )}
              </button>
            </IconTooltip>

            {showNotifications && (
              <NotificationDropdown
                alerts={notifications}
                loading={notifLoading}
                viewAllPath={viewAllPath}
                onClose={() => setShowNotifications(false)}
                onMarkRead={handleMarkRead}
                onMarkAllRead={handleMarkAllRead}
              />
            )}
          </div>
          <div className="w-px h-6 bg-border mx-1" />

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
              {isAdmin ? "A" : getInitials(user?.fullName)}
            </div>
            <div className="text-right hidden xl:block">
              <p className="text-sm font-medium text-foreground leading-tight">
                {user?.fullName}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {roleName}
              </p>
            </div>
            <IconTooltip content="Logout" side="bottom">
              <button
                onClick={() => setShowLogoutModal(true)}
                className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-secondary transition-colors text-muted-foreground"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </IconTooltip>
          </div>
        </div>
      </header>

      {/* Logout Confirmation Modal */}
      <LogoutModel
        showLogoutModal={showLogoutModal}
        setShowLogoutModal={setShowLogoutModal}
      />

      <StoreCloseModal
        open={restrictionModal.open}
        onOpenChange={(open) => setRestrictionModal(prev => ({ ...prev, open }))}
        title={restrictionModal.title}
        message={restrictionModal.message}
      />
    </>
  );
};
